import React from "react";

import { usePutCustomerFilterQuery } from "@tiny/api";
import { useRefreshRegister } from "@tiny/components/src/RefreshProvider";
import useSearchable from "@tiny/components/src/hooks/useSearchable";
import useSelect from "@tiny/components/src/hooks/useSelect";
import { pageableDefault } from "./SubscribersList.helpers";
import {
  CustomerStatuses,
  CustomerStatus,
  SubscribersListProps,
} from "./SubscribersList.types";
import { CustomerInfoView } from "@tiny/api";
import { useApi } from "@tiny/api";
import {
  downloadFileData,
  downloadFile,
  ResponseHeaders,
} from "@tiny/components/src/utils/fileUtils";
import { useMessages } from "@tiny/components";
import {
  extractBackendError,
  getErrorMessage,
} from "@tiny/components/src/utils/errorUtils";
import { useTinyHost } from "@tiny/host-contract";

export function useSubscribersList(props: SubscribersListProps) {
  const api = useApi();
  const {
    segmentId,
    defaultPageable = pageableDefault,
    onAfterDelete,
    pageName = "",
  } = props;

  const { showErrorMessage } = useMessages();

  const { navigate } = useTinyHost();
  const { capabilities } = useTinyHost();
  const { isShopifyPlan } = capabilities;

  const [search, setSearch] = React.useState("");

  const [deleteOpen, setDeleteOpen] = React.useState(false);

  const [filterOpen, setFilterOpen] = React.useState(false);

  const [filterStatuses, setFilterStatuses] = React.useState<CustomerStatuses>(
    []
  );

  const [filterTags, setFilterTags] = React.useState<string[]>([]);

  const [columnsSettingsVisible, setColumnsSettingsVisible] =
    React.useState(false);

  const [selectAllInAudience, setSelectAllInAudience] = React.useState(false);

  const [moveOpen, setMoveOpen] = React.useState(false);

  const [activeCustomer, setActiveCustomer] =
    React.useState<CustomerInfoView>();

  const { pageable, handlePageChange, handleSizeChange, handleSortChange } =
    useSearchable(defaultPageable, pageName);

  const {
    selected,
    handleToggleSelect,
    handleDeselectAll,
    handleAddSelected,
    handleRemoveSelected,
    isAllSelected,
  } = useSelect<CustomerInfoView>({
    isEqual: (a: CustomerInfoView, b: CustomerInfoView) => a.id === b.id,
  });

  const {
    data,
    refetch: refetchSubscribers,
    isLoading,
    isFetching,
  } = usePutCustomerFilterQuery(
    {
      segmentId,
      searchText: search,
      statuses: filterStatuses,
      tags: filterTags,
    },
    pageable,
    {
      keepPreviousData: true,
    }
  );

  useRefreshRegister(async () => {
    await refetchSubscribers();
  });

  const selectSubscriber = React.useCallback(
    (id: string) => {
      setSelectAllInAudience(false);
      const item = data?.content?.find((i) => i.id === id);
      handleToggleSelect(item);
    },
    [data?.content, handleToggleSelect]
  );

  const deselectAllSubscribers = React.useCallback(() => {
    setSelectAllInAudience(false);
    handleDeselectAll();
  }, [handleDeselectAll]);

  const deleteSelectedSubscribers = React.useCallback(async () => {
    setActiveCustomer(undefined);
    setDeleteOpen(true);
  }, []);

  const exportSubscribers = React.useCallback(
    async (type: string, columns: string[]) => {
      if (type === "CSV") {
        const ids = selected.map((item) => item.id!);
        try {
          const { data, headers } = await api.putCustomerFilterExport(
            {
              searchText: search,
              segmentId,
              ids,
              tags: filterTags,
              statuses: filterStatuses,
              columns: columns,
            },
            {
              format: "blob",
              headers: {
                "Access-Control-Expose-Headers": "Content-Disposition",
              },
            }
          );

          // Check if the response is JSON
          const contentType = headers?.["content-type"] || "";

          if (contentType.includes("application/json")) {
            try {
              // Convert Blob to JSON
              const parsedJson = JSON.parse(
                await (data as unknown as Blob).text()
              );
              downloadFile(parsedJson.url);
            } catch (error) {
              console.error("Failed to parse JSON:", error);
            }
          } else {
            const exportHeaders = headers || {};
            if (!exportHeaders?.["content-disposition"]) {
              exportHeaders["content-disposition"] = "fileName=campaigns.csv";
            }
            // Assume it's a file and download it
            downloadFileData(data, exportHeaders as ResponseHeaders);
          }
        } catch (e: any) {
          extractBackendError(e.response).then((be) => {
            showErrorMessage(getErrorMessage(be));
          });
        }
      }
    },
    [filterStatuses, filterTags, search, segmentId, selected, showErrorMessage]
  );

  const toggleAllSubscribers = React.useCallback(
    (checked: boolean) => {
      setSelectAllInAudience(false);
      const items = data?.content;
      if (checked) {
        handleAddSelected(items!);
      } else {
        handleRemoveSelected(items!);
      }
    },
    [data?.content, handleAddSelected, handleRemoveSelected]
  );

  const sortSubscribers = React.useCallback(
    (sort: string[]) => {
      handleSortChange(sort);
    },
    [handleSortChange]
  );

  const searchSubscribers = React.useCallback(
    async (value: string) => {
      setSelectAllInAudience(false);
      handlePageChange(0);
      setSearch(value);
    },
    [handlePageChange]
  );

  const openDelete = React.useCallback((customer: CustomerInfoView) => {
    setActiveCustomer(customer);
    setDeleteOpen(true);
  }, []);

  const closeDelete = React.useCallback(() => {
    setActiveCustomer(undefined);
    setDeleteOpen(false);
  }, []);

  const openFilter = React.useCallback(() => {
    setFilterOpen(true);
  }, []);

  const closeFilter = React.useCallback(() => {
    setFilterOpen(false);
  }, []);

  const successDelete = React.useCallback(() => {
    if (activeCustomer) {
      setActiveCustomer(undefined);
    } else {
      handleDeselectAll();
    }
    refetchSubscribers();
    onAfterDelete?.();
    setDeleteOpen(false);
  }, [activeCustomer, handleDeselectAll, onAfterDelete, refetchSubscribers]);

  const onSelectAllInAudience = React.useCallback(() => {
    setSelectAllInAudience(true);
  }, []);

  const clearSelection = React.useCallback(() => {
    setSelectAllInAudience(false);
    handleDeselectAll();
  }, [handleDeselectAll]);

  const openMove = React.useCallback(() => {
    setMoveOpen(true);
  }, []);

  const closeMove = React.useCallback(() => {
    setMoveOpen(false);
  }, []);

  const successMove = React.useCallback(() => {
    setSelectAllInAudience(false);
    handleDeselectAll();
    refetchSubscribers();
    onAfterDelete?.();
    setMoveOpen(false);
  }, [handleDeselectAll, onAfterDelete, refetchSubscribers]);

  const successEdit = React.useCallback(() => {
    refetchSubscribers();
  }, [refetchSubscribers]);

  const openColumnsSettings = React.useCallback(() => {
    setColumnsSettingsVisible(true);
  }, []);

  const closeColumnsSettings = React.useCallback(() => {
    setColumnsSettingsVisible(false);
  }, []);

  const saveFilters = React.useCallback(
    (statuses: CustomerStatuses, tags: string[]) => {
      setSelectAllInAudience(false);
      setFilterStatuses(statuses);
      setFilterTags(tags);
      closeFilter();
    },
    [closeFilter]
  );

  const deselectTag = React.useCallback((tag: string) => {
    setSelectAllInAudience(false);
    setFilterTags((prev) => prev.filter((item) => item !== tag));
  }, []);

  const onSubscriberClick = React.useCallback(
    (customer: CustomerInfoView) => {
      const url = `/main/customer-history/${customer.email}`;
      if (isShopifyPlan || capabilities.subscriberHistorySameTab) {
        navigate.to(url);
      } else {
        window.open(url, "_blank");
      }
    },
    [isShopifyPlan, capabilities.subscriberHistorySameTab, navigate]
  );

  const deselectStatus = React.useCallback((status: CustomerStatus) => {
    setSelectAllInAudience(false);
    setFilterStatuses((prev) => prev.filter((item) => item !== status));
  }, []);

  const allSubscribersSelected = React.useMemo(() => {
    return isAllSelected(data?.content);
  }, [data?.content, isAllSelected]);

  const selectedSubscribersIds = React.useMemo(() => {
    return selected.map((item) => item.id!);
  }, [selected]);

  const sort = pageable.sort;
  const currentPage = pageable.page;
  const pageSize = pageable.size;
  const isSearching = !!search;
  const subscribers = data?.content || [];
  const totalElements = data?.totalElements || 0;

  const enableMove = props.segmentType === "UPLOAD";

  const isFilterActive =
    !!search || filterStatuses.length > 0 || filterTags.length > 0;

  const canSelectAllInAudience =
    enableMove &&
    !isFilterActive &&
    !selectAllInAudience &&
    allSubscribersSelected &&
    totalElements > selectedSubscribersIds.length;

  return {
    activeCustomer,
    allSubscribersSelected,
    closeDelete,
    currentPage,
    deleteOpen,
    deleteSelectedSubscribers,
    deselectAllSubscribers,
    exportSubscribers,
    handlePageChange,
    handleSizeChange,
    isLoading,
    // Background refetches (search/sort/filter/page) while keepPreviousData
    // still shows the prior list; surfaced as an inline indicator instead of
    // swapping out the mounted header/table, which would drop search focus.
    isRefetching: isFetching,
    isSearching,
    pageSize,
    searchSubscribers,
    selectSubscriber,
    selected,
    selectedSubscribersIds,
    sort,
    sortSubscribers,
    subscribers,
    successDelete,
    successEdit,
    toggleAllSubscribers,
    totalElements,
    openDelete,
    columnsSettingsVisible,
    openColumnsSettings,
    closeColumnsSettings,
    filterOpen,
    openFilter,
    closeFilter,
    saveFilters,
    filterStatuses,
    filterTags,
    deselectTag,
    deselectStatus,
    onSubscriberClick,
    enableMove,
    selectAllInAudience,
    canSelectAllInAudience,
    onSelectAllInAudience,
    clearSelection,
    moveOpen,
    openMove,
    closeMove,
    successMove,
  };
}
